import { ArrowRight, Clock, UserRound } from "lucide-react";
import type { PropertyAuditEntry } from "../../../types/propertyAudit";
import { formatDate } from "../../../utils/formatDate";
import {
  displayPropertyAuditValue,
  getPropertyAuditAuthorName,
  getPropertyAuditFieldLabel,
  getPropertyAuditSourceLabel,
  isRentalCycleAudit,
} from "../../../utils/propertyAuditLabels";

const HEADER_CELL =
  "whitespace-nowrap border-none px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600";
const BODY_CELL =
  "border-none px-3 py-3 align-top text-[13px] text-slate-600";

function SourceBadge({ audit }: { audit: PropertyAuditEntry }) {
  const label = getPropertyAuditSourceLabel(audit);
  const isRentalCycle = isRentalCycleAudit(audit);

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        isRentalCycle
          ? "bg-violet-100 text-violet-700"
          : "bg-sky-100 text-sky-700"
      }`}
    >
      {label}
    </span>
  );
}

function AuditValueChange({
  oldValue,
  newValue,
}: {
  oldValue: string | null;
  newValue: string | null;
}) {
  const formattedOld = displayPropertyAuditValue(oldValue);
  const formattedNew = displayPropertyAuditValue(newValue);
  const hasOld = formattedOld !== "—";
  const hasNew = formattedNew !== "—";

  if (!hasOld && !hasNew) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  if (!hasOld) {
    return (
      <span className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
        {formattedNew}
      </span>
    );
  }

  return (
    <div className="flex min-w-[180px] flex-wrap items-center gap-2">
      <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 line-through decoration-slate-400">
        {formattedOld}
      </span>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
      <span className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
        {formattedNew}
      </span>
    </div>
  );
}

function AuditEventDetails({ audit }: { audit: PropertyAuditEntry }) {
  const event = audit.event;
  if (!event) return null;

  const items = [
    { label: "Inquilino", value: event.tenant },
    { label: "Consultor", value: event.consultant },
    { label: "Motivo", value: event.reason },
    { label: "Valor final", value: event.final_rent_value },
    { label: "Alugado por", value: event.rented_by },
  ].filter((item) => item.value?.trim());

  if (items.length === 0) return null;

  return (
    <dl className="mt-2 grid gap-2 rounded-lg border border-violet-100 bg-violet-50/60 p-3 sm:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">
            {item.label}
          </dt>
          <dd className="mt-0.5 text-xs font-medium text-slate-700">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function AuditChangeCell({ audit }: { audit: PropertyAuditEntry }) {
  return (
    <div>
      <AuditValueChange oldValue={audit.old_value} newValue={audit.new_value} />
      <AuditEventDetails audit={audit} />
    </div>
  );
}

export function PropertyAuditDesktopTable({
  audits,
}: {
  audits: PropertyAuditEntry[];
}) {
  return (
    <div className="hidden overflow-x-auto rounded-xl border border-slate-200 md:block">
      <table className="min-w-[1040px] w-full border-separate border-spacing-0 text-[13px]">
        <thead className="bg-slate-50">
          <tr>
            <th className={`${HEADER_CELL} rounded-tl-xl`}>Data e hora</th>
            <th className={HEADER_CELL}>Tipo</th>
            <th className={HEADER_CELL}>Campo</th>
            <th className={HEADER_CELL}>Alteração</th>
            <th className={`${HEADER_CELL} rounded-tr-xl`}>Autor</th>
          </tr>
        </thead>
        <tbody>
          {audits.map((audit, index) => (
            <tr
              key={audit.id}
              className={
                index % 2 === 0
                  ? "bg-white hover:bg-slate-50/80"
                  : "bg-slate-50/60 hover:bg-slate-100/70"
              }
            >
              <td className={`${BODY_CELL} whitespace-nowrap font-medium text-slate-700`}>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" aria-hidden />
                  {formatDate(audit.created_at)}
                </div>
              </td>
              <td className={BODY_CELL}>
                <SourceBadge audit={audit} />
              </td>
              <td className={`${BODY_CELL} font-medium text-slate-800`}>
                {getPropertyAuditFieldLabel(audit)}
              </td>
              <td className={BODY_CELL}>
                <AuditChangeCell audit={audit} />
              </td>
              <td className={BODY_CELL}>
                <div className="flex items-center gap-2 text-slate-700">
                  <UserRound className="h-4 w-4 text-slate-400" aria-hidden />
                  {getPropertyAuditAuthorName(audit.author)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PropertyAuditMobileList({
  audits,
}: {
  audits: PropertyAuditEntry[];
}) {
  return (
    <div className="space-y-3 md:hidden">
      {audits.map((audit) => (
        <article
          key={audit.id}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-2">
                <SourceBadge audit={audit} />
              </div>
              <p className="text-sm font-semibold text-slate-800">
                {getPropertyAuditFieldLabel(audit)}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                {formatDate(audit.created_at)}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
              <UserRound className="h-3 w-3" aria-hidden />
              {getPropertyAuditAuthorName(audit.author)}
            </span>
          </div>
          <div className="mt-3">
            <AuditChangeCell audit={audit} />
          </div>
        </article>
      ))}
    </div>
  );
}
